/* ============================================================================
   APP CONFIG — resolved from Vite env (VITE_*) with mock-first defaults.

   `mode` selects the data plane:
     - 'mock'   : everything from the bundled mock store (no backend needed)
     - 'hybrid' : real GraphQL via the BFF where it exists, mock for futures
   `authMode` selects how a user signs in:
     - 'demo'     : the prototype identity picker (no backend) — DEFAULT
     - 'firebase' : Option B — per-user Firebase phone sign-in → real QA session
                    via the BFF. Active only when VITE_AUTH_MODE=firebase AND a
                    Firebase web config is present. See documentation/plan/07.
   ============================================================================ */
export type DataMode = 'mock' | 'hybrid';
export type AuthMode = 'demo' | 'firebase';

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  messagingSenderId?: string;
}

export interface AppConfig {
  runtimeTarget: 'web';
  mode: DataMode;
  authMode: AuthMode;
  /** Same-origin Pages Function (BFF) base path. */
  bffPath: string;
  /** Logical environment label (dev/qa/prod). */
  env: string;
  /** Firebase web config (public) for Option B phone auth, or null. */
  firebase: FirebaseWebConfig | null;
}

const env = import.meta.env;

function readFirebase(): FirebaseWebConfig | null {
  const apiKey = env.VITE_FIREBASE_API_KEY;
  if (!apiKey) return null;
  return {
    apiKey,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
    appId: env.VITE_FIREBASE_APP_ID ?? '',
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  };
}

const firebase = readFirebase();
// Firebase auth requires BOTH the opt-in flag and a usable config; otherwise fall back to demo.
const authMode: AuthMode = env.VITE_AUTH_MODE === 'firebase' && firebase ? 'firebase' : 'demo';

export const CONFIG: AppConfig = {
  runtimeTarget: 'web',
  mode: (env.VITE_DATA_MODE as DataMode) || 'mock',
  authMode,
  bffPath: env.VITE_BFF_PATH || '/api',
  env: env.VITE_ENV || 'dev',
  firebase,
};
